import { useScreenViewport } from "@shared/utils.hooks";
import React, { useEffect, useRef } from "react";

import {
  activeLink,
  link,
  navigationContainerVariants,
  navigationItems
} from "./MyAccountNavigation.css";
import { useNavBarItems } from "./accountItems";

type MyAccountNavigationProps = {
  variant: "default" | "legacy";
  isMember: boolean;
};

export const MyAccountNavigation: React.FC<MyAccountNavigationProps> = ({
  variant,
  isMember
}) => {
  const { isMobile } = useScreenViewport();
  const activeItemRef = useRef<HTMLAnchorElement>(null);
  const navBarGroups = useNavBarItems({ isMember });

  useEffect(() => {
    if (!isMobile) return;
    activeItemRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [isMobile]);

  // Identify the group with an active item
  const activeGroup = navBarGroups.find((group) =>
    group.items.some((item) => item.active)
  );

  return (
    <div className={navigationContainerVariants[variant]}>
      {/* Top-level nav bar */}
      <nav className={navigationItems}>
        {navBarGroups.map(({ title, href, items }) => {
          const isActiveGroup = items.some((item) => item.active);
          const singleItem = items.length === 1 ? items[0] : null;

          if (singleItem) {
            return (
              <a
                key={singleItem.href}
                className={isActiveGroup ? activeLink : link}
                href={singleItem.href}
                title={singleItem.title}
                ref={isActiveGroup ? activeItemRef : undefined}
              >
                {singleItem.title}
              </a>
            );
          }

          return (
            <a
              key={title}
              className={isActiveGroup ? activeLink : link}
              href={href ?? items[0].href}
              title={title}
              ref={isActiveGroup ? activeItemRef : undefined}
            >
              {title}
            </a>
          );
        })}
      </nav>

      {/* Sub-navigation bar for active group (if any and has >1 item) */}
      {activeGroup?.items?.length && activeGroup.items.length > 1 && (
        <nav className={navigationItems}>
          {activeGroup.items.map(({ title, href, active }) => (
            <a
              key={href}
              className={active ? activeLink : link}
              href={href}
              title={title}
              ref={active ? activeItemRef : undefined}
            >
              {title}
            </a>
          ))}
        </nav>
      )}
    </div>
  );
};
